// theme.js
import { extendTheme } from '@chakra-ui/react';

const theme = extendTheme({
  // Font configuration - moved from CSS
  fonts: {
    heading: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    mono: "'JetBrains Mono', 'Courier New', monospace",
  },

  // Semantic color tokens
  semanticTokens: {
    colors: {
      'brand.primary': 'blue.500',
      'brand.secondary': 'blue.600',
      'text.primary': 'gray.700',
      'text.secondary': 'gray.500',
      'text.muted': 'gray.400',
      'bg.primary': 'white',
      'bg.secondary': 'gray.50',
      'border.primary': 'gray.200',
      'border.secondary': 'gray.300',
    },
  },

  colors: {
    brand: {
      50: '#e6f3ff',
      100: '#b3d9ff',
      200: '#80bfff',
      300: '#4da6ff',
      400: '#1a8cff',
      500: '#0066cc', // primary
      600: '#0052a3',
      700: '#003d7a',
      800: '#002952',
      900: '#001429',
    },
    corporate: {
      primary: '#333333',
      secondary: '#FF8923',
    },
    chat: {
      bg: '#fafafa',           // Light gray background
      sidebarBg: '#ffffff',    // White sidebar background
      sidebarText: '#2d3748',  // Dark text on light background
      userBg: '#fdfdfd',       // Light gray for user messages (was green #e6fffa)
      botBg: '#ffffff',        // White for bot messages
      border: '#e2e8f0',       // Light border
      sidebarBorder: '#e2e8f0', // Sidebar border
      sidebarHover: '#f7fafc', // Subtle hover state
      sidebarActive: '#e6f3ff', // Active conversation highlight
    }
  },

  // Consistent spacing scale
  space: {
    '18': '4.5rem',
    '22': '5.5rem',
  },


  styles: {
    global: {
      // === FOUNDATION ===
      html: {
        textRendering: 'optimizeLegibility',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      },
      
      body: {
        fontFamily: 'body',
        fontOpticalSizing: 'auto',
        fontFeatureSettings: "'cv02', 'cv03', 'cv04', 'cv09', 'dlig'",
        lineHeight: '1.3',
        fontWeight: 400,
        color: 'text.primary',
        bg: 'bg.primary',
        // Prevent pull-to-refresh reload and horizontal page bounce on mobile.
        // The chat scroll container handles its own scrolling.
        overscrollBehaviorY: 'none',
      },

      // === HEADINGS ===

      'h1': {
          fontFamily: 'heading',
          lineHeight: '1.2',
          fontWeight: 800,
          mt: 8,    // 2rem
          mb: 4,    // 1rem
          fontSize: '2xl',
      },
      'h2': {
          fontFamily: 'heading',
          lineHeight: '1.2',
          fontWeight: 800,
          mt: 6,    // 1.5rem
          mb: 3,    // 0.75rem
          fontSize: 'xl',
      },
      'h3': {
          fontFamily: 'heading',
          lineHeight: '1.2',
          fontWeight: 800,
          mt: 5,    // 1.25rem
          mb: 3,    // 0.75rem
          fontSize: 'lg',
      },
      'h4, h5, h6': {
          fontFamily: 'heading',
          lineHeight: '1.2',
          fontWeight: 700,
          mt: 4,    // 1rem
          mb: 2,    // 0.5rem
          fontSize: 'md',
      },

      // === TEXT ELEMENTS ===
      'p': {
        fontFamily: 'body',
        lineHeight: '1.3',
        fontWeight: 400,
        fontSize: 'md',
        color: 'text.primary',
      },

      'strong, b': {
        fontWeight: 600,
      },

      // === CODE BLOCKS ===
      'pre': {
        fontFamily: 'mono',
        lineHeight: '1.2',
        fontWeight: 400,
      },
      
      'pre *': {
        fontFamily: 'inherit',
        lineHeight: 'inherit',
        fontWeight: 'inherit',
      },


      // Override inline code styles when inside pre
      'pre code': {
        background: 'transparent !important',
        border: 'none !important',
        padding: '0 !important',
        margin: '0 !important',
        borderRadius: 'none !important',
        fontFamily: 'inherit',
        fontSize: 'inherit',
        color: 'inherit',
      },

      'code': {
        fontFamily: 'mono',
        lineHeight: '1.2',
        fontWeight: 400,
      },

      // === LISTS ===
      'ul': {
        fontFamily: 'body',
        fontSize: 'md',
        lineHeight: '1.3',
        color: 'text.primary',
        mt: 2,
        mb: 4,
        ml: 0,
        listStyleType: 'none',
        '& > li': {
          position: 'relative',
          paddingLeft: '1.2em',
          marginBottom: '0.15em',
          '&::before': {
            content: '"–"',
            position: 'absolute',
            left: 0,
            top: 0,
            color: 'text.primary',
            fontWeight: 'inherit',
          },
        },
      },

      'ol': {
        fontFamily: 'body',
        fontSize: 'md',
        lineHeight: '1.3',
        color: 'text.primary',
        mt: 2,
        mb: 4,
        ml: 0,
        pl: 4,
        listStyleType: 'decimal',
        listStylePosition: 'outside',
        '& > li': {
          marginBottom: '0.25em',
          paddingLeft: '0.5em',
        },
      },

      'li': {
        fontSize: 'md',
        lineHeight: '1.3',
        color: 'text.primary',
        mt: 0,
        mb: 1,
        mx: 0,
      },

      // === BLOCKQUOTES ===
      'blockquote': {
        borderLeft: '4px solid',
        borderColor: 'border.primary',
        backgroundColor: 'bg.secondary',
        pl: 4,
        py: 3,
        my: 4,
        fontStyle: 'italic',
        fontFamily: 'body',
        borderRadius: '0 4px 4px 0',
        color: 'text.primary',
      },

      // === HORIZONTAL RULES ===
      'hr': {
        border: 'none',
        height: '1px',
        width: '25%',
        mx: 'auto',
        my: 6,
        backgroundColor: 'gray.400',
        borderRadius: 'full',
      },

      // === LINKS ===
      'a': {
        color: 'brand.500',
        textDecoration: 'underline',
        fontFamily: 'body',
        display: 'inline',
        _hover: {
          color: 'brand.600',
          textDecoration: 'underline',
        },
      },

      // === IMAGES ===
      'img': {
      },

      // === TABLES ===
      'table': {
        fontFamily: 'body',
        fontSize: 'sm',
        color: 'text.primary',
        width: '100%',
        borderCollapse: 'collapse',
        // my: 4,
        border: '1px solid',
        borderColor: 'border.primary',
        borderRadius: 'md',
        bg: 'bg.primary',
      },

      'thead': {
        bg: 'bg.secondary',
      },

      'tr': {
        borderBottom: '1px solid',
        borderColor: 'border.primary',
        _hover: {
          bg: 'gray.100',
        },
      },

      'th': {
        p: 3,
        fontWeight: 600,
        fontFamily: 'body',
        color: 'text.primary',
        borderRight: '1px solid',
        borderColor: 'border.primary',
        _last: { borderRight: 'none' },
      },

      'td': {
        p: 3,
        fontFamily: 'body',
        color: 'text.primary',
        borderRight: '1px solid',
        borderColor: 'border.primary',
        _last: { borderRight: 'none' },
      },

      // === FORM ELEMENTS ===
      'input, textarea, select': {
        fontFamily: 'mono',
        lineHeight: '1.2',
        fontWeight: 400,
        fontSize: { base: 'md', md: 'sm' },
      },

      'label': {
        fontFamily: 'body',
        fontWeight: 500,
      },

      'button': {
        fontFamily: 'body',
        fontWeight: 400,
      },
    },
  },

  textStyles: {
    // Typography variants with weight control
    title: {
      fontFamily: 'heading',
      lineHeight: '1.2',
      fontWeight: 800,
    },
    
    heading: {
      fontFamily: 'heading',
      lineHeight: '1.2', 
      fontWeight: 800,
    },
    
    subheading: {
      fontFamily: 'heading',
      lineHeight: '1.2',
      fontWeight: 700,
    },
    
    body: {
      fontFamily: 'body',
      lineHeight: '1.3',
      fontWeight: 400,
    },
    
    emphasis: {
      fontFamily: 'body', 
      lineHeight: '1.3',
      fontWeight: 600,
    },
    
    mono: {
      fontFamily: 'mono',
      lineHeight: '1.2',
      fontWeight: 400,
      // fontFeatureSettings: '',
    },
  },

  // === COMPONENTS = LAYOUT ONLY ===
  components: {
    Button: {
      defaultProps: {
        fontFamily: 'body',
      },
      variants: {
        solid: {
          fontWeight: '500',
        },
      },
    },
    Text: {
      baseStyle: {
        fontFamily: 'body',
      },
    },
    FormLabel: {
      baseStyle: {
        fontFamily: 'body',
        fontWeight: '500',
      },
    },
    Input: {
      variants: {
        outline: {
          field: {
            borderWidth: '1px',
            fontSize: { base: 'md', md: 'sm' },
            _hover: {
              borderColor: 'border.secondary',
            },
            _focus: {
              borderWidth: '1px',
              borderColor: 'brand.500',
              bg: 'bg.primary',
            },
          },
        },
        // Add new variant for message input
        messageInput: {
          field: {
            borderWidth: '1px',
            borderColor: 'border.primary',
            bg: 'bg.primary',
            fontFamily: 'mono',
            fontSize: { base: 'md', md: 'sm' },
            fontWeight: '500',
            lineHeight: '1.2',
            _hover: {
              borderColor: 'border.secondary',
            },
            _focus: {
              borderWidth: '1px',
              borderColor: 'brand.500',
              bg: 'bg.primary',
              boxShadow: 'none',
            },
            _placeholder: {
              color: 'text.muted',
              fontWeight: '400',
            },
          },
        },
      },
    },

    Select: {
      variants: {
        outline: {
          field: {
            borderWidth: '1px',
            fontSize: { base: 'md', md: 'sm' },
            _hover: {
              borderColor: 'border.secondary',
            },
            _focus: {
              borderWidth: '1px',
              borderColor: 'brand.500',
              bg: 'bg.primary',
            },
          },
        },
        // Add this if you want custom dropdown styling
        messageInput: {
          field: {
            borderWidth: '1px',
            borderColor: 'border.primary',
            bg: 'bg.primary',
            fontFamily: 'mono',
            fontSize: 'sm',
            fontWeight: '400',
            _hover: {
              borderColor: 'border.secondary',
            },
            _focus: {
              borderWidth: '1px',
              borderColor: 'brand.500',
              bg: 'bg.primary',
            },
          },
        },
      },
    },

    Textarea: {
      variants: {
        outline: {
          borderWidth: '1px',
          borderColor: 'border.primary',
          bg: 'bg.primary',
          _hover: {
            borderWidth: '1px',
            borderColor: 'border.secondary',
            bg: 'bg.primary',
            fontSize: { base: 'md', md: 'sm' },
          },
          _focus: {
            borderWidth: '1px',
            borderColor: 'border.secondary',
            bg: 'bg.primary',
            boxShadow: 'none',
            transform: 'none',
            transition: 'none',
          },
        },
        // Add new variant for message input
        messageInput: {
          borderWidth: '1px',
          borderColor: 'border.primary',
          bg: 'bg.primary',
          fontFamily: 'mono',
          fontSize: { base: 'md', md: 'sm' },
          fontWeight: '500',
          lineHeight: '1.2',
          _hover: {
            borderWidth: '1px',
            borderColor: 'border.secondary',
            bg: 'bg.primary',
          },
          _focus: {
            borderWidth: '1px',
            borderColor: 'brand.500',
            bg: 'bg.primary',
            boxShadow: 'none',
            transform: 'none',
            transition: 'none',
          },
          _placeholder: {
            color: 'text.muted',
            fontWeight: '400',
          },
        },
      },
    },

  Code: {
    baseStyle: {
      fontFamily: "mono",
      fontWeight: 400,
      lineHeight: "1.2",
    },
    variants: {
      inline: {
        display: "inline",
        fontSize: "sm",
        px: 1,
        py: 0.5,
        borderRadius: "sm",
        bg: "gray.50",
        border: "1px solid",
        borderColor: "gray.200",
        color: "gray.800",
      },
      block: {
        display: "block",
        fontSize: "sm",
        px: 0,
        py: 0,
        mx: 0,
        my: 3,
        border: "none",
        borderRadius: "none",
        boxShadow: "none",
        bg: "transparent",
        color: "inherit",
      },
    },
    defaultProps: {
      variant: "inline",
    },
  },


    Pre: {
      baseStyle: {
        borderRadius: "none",
        background: "none",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        overflowX: "auto",
        border: "none",
        px: 0,
        py: 2,
        fontSize: "sm",
        color: "inherit",
        // Override any code styling inside pre
        '& code': {
          background: 'transparent !important',
          border: 'none !important',
          padding: '0 !important',
          margin: '0 !important',
          borderRadius: 'none !important',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          color: 'inherit',
        }
      },
    },

   },

});

export default theme;